import {
  ApartmentSectionOrderId,
  IApartmentSectionsOrder,
  ICustomSection,
} from '@smarttypes/hotel';
import { defaultTo, get } from 'lodash';

import { parseCustomSectionOrderId } from './apartment-sections-order';

type CustomSectionServiceType = 'info' | 'upsell';

function extractCustomSectionIdsFromOrder(sectionsOrder?: IApartmentSectionsOrder): {
  stay: string[];
  upsell: string[];
} {
  const extract = (order: ApartmentSectionOrderId[] = []): string[] =>
    order.map(parseCustomSectionOrderId).filter((id): id is string => !!id);

  return {
    stay: extract(sectionsOrder?.stay),
    upsell: extract(sectionsOrder?.upsell),
  };
}

function getSourceLanguage(
  allCustomSections: Record<string, ICustomSection[]>,
  allLanguages: string[],
  sectionsOrder?: IApartmentSectionsOrder,
  preferredSourceLang?: string,
): string | undefined {
  if (preferredSourceLang) {
    return preferredSourceLang;
  }

  const canonicalIds = new Set([
    ...extractCustomSectionIdsFromOrder(sectionsOrder).stay,
    ...extractCustomSectionIdsFromOrder(sectionsOrder).upsell,
  ]);

  const languages = [...allLanguages, ...Object.keys(allCustomSections)];

  if (canonicalIds.size > 0) {
    for (const language of languages) {
      const sections = get(allCustomSections, language, []) as ICustomSection[];
      if (sections.some(section => section.sectionId && canonicalIds.has(section.sectionId))) {
        return language;
      }
    }
  }

  for (const language of languages) {
    const sections = get(allCustomSections, language, []) as ICustomSection[];
    if (sections.length > 0) {
      return language;
    }
  }

  return undefined;
}

function getCanonicalSections(
  allCustomSections: Record<string, ICustomSection[]>,
  sourceLang: string,
): ICustomSection[] {
  return (get(allCustomSections, sourceLang, []) as ICustomSection[]).filter(section => section.sectionId);
}

function filterByServiceType(sections: ICustomSection[], serviceType: CustomSectionServiceType): ICustomSection[] {
  if (serviceType === 'upsell') {
    return sections.filter(section => section.serviceType === 'upsell');
  }

  return sections.filter(section => (section.serviceType ?? 'info') === 'info');
}

type CustomSectionTranslation = Pick<ICustomSection, 'title' | 'description' | 'price'>;

function getTranslationByCanonicalId(
  targetSections: ICustomSection[],
  canonicalSection: ICustomSection,
  canonicalIndex: number,
  canonicalSections: ICustomSection[],
): CustomSectionTranslation {
  const sectionId = canonicalSection.sectionId as string;
  const direct = targetSections.find(section => section.sectionId === sectionId);
  if (direct) {
    return {
      title: direct.title,
      description: direct.description,
      price: direct.price,
    };
  }

  const serviceType: CustomSectionServiceType =
    canonicalSection.serviceType === 'upsell' ? 'upsell' : 'info';
  const canonicalSet = new Set(canonicalSections.map(section => section.sectionId));
  const orphaned = filterByServiceType(
    targetSections.filter(section => section.sectionId && !canonicalSet.has(section.sectionId)),
    serviceType,
  );
  const sameTypeCanonical = filterByServiceType(canonicalSections, serviceType);
  const indexInType = sameTypeCanonical.findIndex(section => section.sectionId === sectionId);
  const legacy = indexInType >= 0 ? orphaned[indexInType] : orphaned[canonicalIndex];

  return {
    title: legacy?.title ?? '',
    description: legacy?.description ?? '',
    price: legacy?.price,
  };
}

function buildLocalizedSection(
  canonicalSection: ICustomSection,
  translation: CustomSectionTranslation,
  lang: string,
  sourceLang: string,
): ICustomSection {
  const serviceType = canonicalSection.serviceType ?? 'info';

  return {
    sectionId: canonicalSection.sectionId,
    icon: canonicalSection.icon,
    highlightOnHomeScreen: canonicalSection.highlightOnHomeScreen,
    serviceType,
    active: defaultTo(canonicalSection.active, true),
    cover: canonicalSection.cover,
    title:
      translation.title ??
      (lang === sourceLang ? canonicalSection.title : '') ??
      '',
    description:
      translation.description ??
      (lang === sourceLang ? canonicalSection.description : '') ??
      '',
    ...(serviceType === 'upsell'
      ? {
          price:
            translation.price ??
            (lang === sourceLang ? canonicalSection.price : undefined),
        }
      : {}),
  };
}

export function alignCustomSectionsForLanguage(
  allCustomSections: Record<string, ICustomSection[]>,
  lang: string,
  allLanguages: string[],
  sectionsOrder?: IApartmentSectionsOrder,
  sourceLang?: string,
): ICustomSection[] {
  const resolvedSourceLang =
    sourceLang ?? getSourceLanguage(allCustomSections, allLanguages, sectionsOrder);

  if (!resolvedSourceLang) {
    return (get(allCustomSections, lang, []) as ICustomSection[]) ?? [];
  }

  const canonicalSections = getCanonicalSections(allCustomSections, resolvedSourceLang);
  if (!canonicalSections.length) {
    return (get(allCustomSections, lang, []) as ICustomSection[]) ?? [];
  }

  const targetSections = get(allCustomSections, lang, []) as ICustomSection[];

  return canonicalSections.map((canonicalSection, index) => {
    const translation = getTranslationByCanonicalId(
      targetSections,
      canonicalSection,
      index,
      canonicalSections,
    );

    return buildLocalizedSection(canonicalSection, translation, lang, resolvedSourceLang);
  });
}

export function alignCustomSectionsForAllLanguages(
  allCustomSections: Record<string, ICustomSection[]>,
  allLanguages: string[],
  sectionsOrder?: IApartmentSectionsOrder,
  preferredSourceLang?: string,
): Record<string, ICustomSection[]> {
  const sourceLang = getSourceLanguage(
    allCustomSections,
    allLanguages,
    sectionsOrder,
    preferredSourceLang,
  );
  if (!sourceLang) {
    return allCustomSections;
  }

  const languages = [
    ...allLanguages,
    ...Object.keys(allCustomSections).filter(language => !allLanguages.includes(language)),
  ];

  return [...new Set(languages)].reduce(
    (acc, language) => {
      acc[language] = alignCustomSectionsForLanguage(
        allCustomSections,
        language,
        allLanguages,
        sectionsOrder,
        sourceLang,
      );
      return acc;
    },
    {} as Record<string, ICustomSection[]>,
  );
}
